import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Download, TrendingUp } from "lucide-react";
import { fmtINR } from "@/lib/format";

export default function BOMRevAuditor() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/ops/bom/revision-audit").then(r => setData(r.data)).catch(() => setData({ rows: [], boms_with_revision_change: 0 })); }, []);

  const exportCSV = () => {
    if (!data) return;
    const hdr = "BOM,Revision,Issues,Total Value,FG Qty Planned,First Seen,Last Seen,Rev Changed";
    const rows = data.rows.map(r => `"${r.bom_name}",${r.revision},${r.issues},${r.total_value},${r.fg_qty_planned},${r.first_seen},${r.last_seen},${r.revision_changed_in_month ? "YES" : "NO"}`);
    const blob = new Blob([hdr + "\n" + rows.join("\n")], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `bom_rev_audit_${data.month}.csv`; a.click();
  };

  if (!data) return <div className="p-6 text-muted-foreground">Loading BOM revision audit…</div>;

  // Sort — flagged first, then by BOM name + revision
  const sorted = [...(data.rows || [])].sort((a, b) => (b.revision_changed_in_month - a.revision_changed_in_month) || a.bom_name.localeCompare(b.bom_name));

  return (
    <div className="p-6 space-y-5" data-testid="bom-rev-auditor">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-accent flex items-center gap-1.5"><TrendingUp className="w-3.5 h-3.5" /> BOM Revision Auditor</div>
          <h1 className="font-heading text-3xl font-bold mt-1">BOM Revisions · {data.month}</h1>
          <div className="text-sm text-muted-foreground mt-1">
            <b className="text-amber-600">{data.boms_with_revision_change}</b> BOM{data.boms_with_revision_change !== 1 ? "s" : ""} used with more than one revision this month · <b>{data.total_bom_issues || 0}</b> total issues.
          </div>
        </div>
        <Button onClick={exportCSV} variant="outline" className="rounded-sm gap-1.5" data-testid="bom-rev-export"><Download className="w-3.5 h-3.5" /> Export CSV</Button>
      </div>

      <Card className="rounded-sm border overflow-hidden">
        <table className="w-full text-sm min-w-[900px]">
          <thead className="text-[10px] font-mono uppercase text-muted-foreground bg-muted/40">
            <tr>
              <th className="p-2 text-left">Flag</th>
              <th className="p-2 text-left">BOM</th>
              <th className="p-2 text-right">Rev</th>
              <th className="p-2 text-right">Issues</th>
              <th className="p-2 text-right">Total Value</th>
              <th className="p-2 text-right">FG Qty Planned</th>
              <th className="p-2 text-left">First Seen</th>
              <th className="p-2 text-left">Last Seen</th>
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 && <tr><td colSpan={8} className="p-8 text-center text-muted-foreground text-sm">No BOM issues this month yet.</td></tr>}
            {sorted.map((r, i) => (
              <tr key={i} className={`border-t ${r.revision_changed_in_month ? "bg-amber-500/5" : ""}`} data-testid={`bom-rev-row-${r.bom_id}-${r.revision}`}>
                <td className="p-2">{r.revision_changed_in_month && <AlertTriangle className="w-4 h-4 text-amber-600" title="Revision changed mid-month" />}</td>
                <td className="p-2 font-medium">{r.bom_name}</td>
                <td className="p-2 text-right font-mono">r{r.revision}</td>
                <td className="p-2 text-right font-mono">{r.issues}</td>
                <td className="p-2 text-right font-mono">{fmtINR(r.total_value)}</td>
                <td className="p-2 text-right font-mono">{r.fg_qty_planned}</td>
                <td className="p-2 text-[11px] font-mono">{r.first_seen}</td>
                <td className="p-2 text-[11px] font-mono">{r.last_seen}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
